import { getApiBaseUrl } from '../config/api';

const TOKEN_KEY = 'admin_token';

function resolveUrl(input: RequestInfo | URL): RequestInfo | URL {
  if (typeof input !== 'string') return input;
  if (input.startsWith('http://') || input.startsWith('https://')) return input;
  return `${getApiBaseUrl()}${input.startsWith('/') ? '' : '/'}${input}`;
}

function targetsBackend(url: RequestInfo | URL): boolean {
  const s = typeof url === 'string' ? url : url instanceof URL ? url.toString() : '';
  return s.startsWith(getApiBaseUrl());
}

export async function apiFetch(
  input: RequestInfo | URL,
  init: RequestInit = {}
): Promise<Response> {
  const url = resolveUrl(input);
  const headers = new Headers(init.headers || {});

  if (targetsBackend(url)) {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  const response = await fetch(url, { ...init, headers });

  if (response.status === 401 && targetsBackend(url)) {
    window.dispatchEvent(new CustomEvent('admin:unauthorized'));
  }

  return response;
}

// Transient statuses worth a retry. 429 is the common one: the Booqable proxy
// forwards Booqable's own rate-limit responses, which arrive in short bursts
// (e.g. several order refetches fired back-to-back after a status transition).
const RETRYABLE_STATUSES = new Set([429, 502, 503, 504]);

/**
 * apiFetch with exponential backoff on transient failures (429 / 5xx gateway).
 *
 * Use for idempotent GETs ONLY — retrying a POST/PUT on a 429 could double-apply
 * the mutation. Without this, a single Booqable 429 on the order page's heavy
 * include-refetch threw straight to the fatal "Error loading order" screen even
 * though the throttle clears within a second or two.
 */
export async function apiFetchWithRetry(
  input: RequestInfo | URL,
  init: RequestInit = {},
  opts: { retries?: number; baseDelayMs?: number } = {}
): Promise<Response> {
  const retries = opts.retries ?? 3;
  const baseDelayMs = opts.baseDelayMs ?? 600;
  let response = await apiFetch(input, init);
  for (let attempt = 0; attempt < retries && RETRYABLE_STATUSES.has(response.status); attempt++) {
    await new Promise((r) => setTimeout(r, baseDelayMs * 2 ** attempt));
    response = await apiFetch(input, init);
  }
  return response;
}
